/**
 * useAssetCatalog.ts
 * TanStack Query data layer for the merged Asset Catalog screen
 * (Project Types rail + Assets table). Mirrors the patterns in
 * features/user/hooks/useUsers.ts.
 *
 * The Project Type / Asset *forms* submit to the API themselves and then call
 * their `onAdd` callback, so this module only owns: the two list queries, the
 * toggle/activate mutations, and the invalidation helpers the page calls after
 * a form save.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import projectTypeService, { type ProjectType } from '@/services/projectTypeService';
import { assetService } from '@/services/assetService';
import type { Asset, AssetFilterParams } from '@/types/asset';

export const PROJECT_TYPES_KEY = ['projectTypes', 'catalog'] as const;
export const ASSETS_BY_TYPE_KEY = ['assets', 'byType'] as const;

export type CatalogStatus = 'active' | 'inactive' | null;
export type CatalogCategory = 'wind' | 'solar' | 'hybrid' | 'bess' | null;

// ─── Project Types (left rail) ─────────────────────────────────────────────

export interface ProjectTypesParams {
  search: string;
  status: CatalogStatus;
  category: CatalogCategory;
}

export function useProjectTypesQuery(p: ProjectTypesParams) {
  const query = useQuery({
    queryKey: [...PROJECT_TYPES_KEY, p],
    queryFn: async (): Promise<ProjectType[]> => {
      const res = await projectTypeService.getProjectTypes({
        limit: 200,
        sortBy: 'name:asc',
        ...(p.search ? { search: p.search } : {}),
        ...(p.status ? { status: p.status } : {}),
        ...(p.category ? { category: p.category } : {}),
      });
      return (res?.data ?? []) as ProjectType[];
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  return { ...query, types: query.data ?? [] };
}

export function useProjectTypeMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: PROJECT_TYPES_KEY });

  const activate = useMutation({
    mutationFn: (id: string) => projectTypeService.activateProjectType(id),
    onSuccess: invalidate,
  });
  const deactivate = useMutation({
    mutationFn: (id: string) => projectTypeService.deactivateProjectType(id),
    onSuccess: invalidate,
  });

  return { activate, deactivate, invalidate };
}

// ─── Assets for the selected type (right pane) ─────────────────────────────

export interface AssetsByTypeParams {
  projectTypeId: string | null;
  page: number;
  pageSize: number;
  search: string;
  status: CatalogStatus;
  category: string | null;
  sortField: string;
  sortOrder: 'asc' | 'desc';
}

export function useAssetsByTypeQuery(p: AssetsByTypeParams) {
  const query = useQuery({
    queryKey: [...ASSETS_BY_TYPE_KEY, p],
    queryFn: () => {
      const typeId = p.projectTypeId ?? undefined;
      const params: AssetFilterParams = {
        // Send both keys: older backend builds read `projectType`, newer read
        // `projectTypeId`. The controller accepts either, so this is safe.
        projectTypeId: typeId,
        projectType: typeId,
        page: p.page,
        limit: p.pageSize,
        sortBy: `${p.sortField}:${p.sortOrder}`,
        ...(p.search ? { search: p.search } : {}),
        ...(p.status ? { status: p.status } : {}),
        ...(p.category ? { category: p.category } : {}),
      };
      return assetService.getAssets(params);
    },
    enabled: !!p.projectTypeId,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
  return {
    ...query,
    assets: query.data?.assets ?? [],
    totalDocs: query.data?.totalDocs ?? 0,
  };
}

export function useAssetMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ASSETS_BY_TYPE_KEY });

  const toggleStatus = useMutation({
    mutationFn: (id: string) => assetService.toggleAssetStatus(id),
    onSuccess: invalidate,
  });

  return { toggleStatus, invalidate };
}

export type { Asset, ProjectType };
